import { useCallback, useMemo } from "react";

import { useLocation } from "react-router-dom";

import usePeriodFilter from "@/src/hooks/usePeriodFilter";
import useUpdateSearchParams from "@/src/hooks/useUpdateSearchParams";

import type { Moment } from "moment";

import type { OfferAnalyticsFilterValues } from "./offerAnalytics.types";

interface UseOfferAnalyticsFiltersReturn {
  filters: OfferAnalyticsFilterValues;
  setPeriod: (startDate: Moment, endDate: Moment) => void;
  setOfferUuid: (offerUuid?: string) => void;
}

/** Period and offer filters of the offer analytics page, synced with `?from=&to=&offer=`. */
function useOfferAnalyticsFilters(
  knownOfferUuidList?: readonly string[],
): UseOfferAnalyticsFiltersReturn {
  const location = useLocation();
  const { period, setPeriod } = usePeriodFilter();
  const updateSearchParams = useUpdateSearchParams();
  const requestedOfferUuid = useMemo(
    () => new URLSearchParams(location.search).getAll("offer").find(Boolean),
    [location.search],
  );
  const offerUuid =
    requestedOfferUuid &&
    (!knownOfferUuidList || knownOfferUuidList.includes(requestedOfferUuid))
      ? requestedOfferUuid
      : undefined;

  const filters = useMemo(
    () => ({ ...period, offerUuid }),
    [period, offerUuid],
  );

  const setOfferUuid = useCallback(
    (newOfferUuid?: string): void => {
      updateSearchParams({ offer: newOfferUuid });
    },
    [updateSearchParams],
  );

  return { filters, setPeriod, setOfferUuid };
}

export default useOfferAnalyticsFilters;
